import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, apiError } from "@/lib/api";

const Field = ({ label, htmlFor, children }) => (
  <div className="flex flex-col gap-2 text-left">
    <label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.15em] text-white/60 font-body font-medium">{label}</label>
    {children}
  </div>
);

const Consent = ({ id, checked, onChange, children }) => (
  <label htmlFor={id} className="flex items-start gap-3 text-left cursor-pointer">
    <Checkbox id={id} data-testid={id} checked={checked} onCheckedChange={(v) => onChange(v === true)}
      className="mt-0.5 rounded-[6px] border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-black" />
    <span className="text-xs text-white/70 font-body font-light leading-relaxed">{children}</span>
  </label>
);

export const BookingSuccess = ({ dateLong, title }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
    data-testid="booking-success" className="glass-strong rounded-[1.5rem] p-10 flex flex-col items-center text-center gap-4">
    <div className="glass rounded-full w-14 h-14 flex items-center justify-center"><Check className="w-6 h-6 text-white" /></div>
    <h2 className="font-heading italic text-white text-3xl tracking-[-1px]">{title}</h2>
    <p className="text-sm text-white/80 font-body font-light max-w-xs">Ti aspettiamo il {dateLong} — porta con te un documento d'identità.</p>
    <p className="text-xs text-white/50 font-body font-light max-w-xs">La prenotazione non garantisce l'ingresso. Si richiede un abbigliamento curato.</p>
  </motion.div>
);

const EMPTY = { nome: "", cognome: "", telefono: "", anno: "", lista: "", privacyConsent: false, marketingConsent: false };

export const BookingForm = ({ liste, dateLong, successTitle }) => {
  const [values, setValues] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.lista) return setError("Seleziona una lista.");
    if (!values.privacyConsent) return setError("Devi accettare l'informativa privacy per prenotare.");
    setError("");
    setStatus("submitting");
    try {
      await axios.post(`${API}/bookings`, { ...values, anno: Number(values.anno) });
      setStatus("success");
    } catch (err) {
      setError(apiError(err));
      setStatus("idle");
    }
  };

  if (status === "success") return <BookingSuccess dateLong={dateLong} title={successTitle} />;

  return (
    <form onSubmit={submit} data-testid="booking-form" className="glass-strong rounded-[1.5rem] p-8 flex flex-col gap-5">
      <Field label="Nome" htmlFor="nome">
        <input id="nome" data-testid="booking-nome-input" className="glass-input" required value={values.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Il tuo nome" />
      </Field>
      <Field label="Cognome" htmlFor="cognome">
        <input id="cognome" data-testid="booking-cognome-input" className="glass-input" required value={values.cognome} onChange={(e) => set("cognome", e.target.value)} placeholder="Il tuo cognome" />
      </Field>
      <Field label="Numero di Telefono" htmlFor="telefono">
        <input id="telefono" data-testid="booking-telefono-input" className="glass-input" type="tel" inputMode="tel" required value={values.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="Es. 333 1234567" />
      </Field>
      <Field label="Anno di Nascita" htmlFor="anno">
        <input id="anno" data-testid="booking-anno-input" className="glass-input" type="number" inputMode="numeric" required min="1940" max={new Date().getFullYear()} value={values.anno} onChange={(e) => set("anno", e.target.value)} placeholder="Es. 2002" />
      </Field>
      <Field label="Lista" htmlFor="lista">
        <Select value={values.lista} onValueChange={(v) => set("lista", v)}>
          <SelectTrigger id="lista" data-testid="booking-lista-select" className="glass-input h-auto px-5 py-3.5 rounded-2xl border-0 shadow-none data-[placeholder]:text-white/45">
            <SelectValue placeholder="Seleziona una lista" />
          </SelectTrigger>
          <SelectContent className="bg-black/90 backdrop-blur-xl border-white/10 text-white rounded-2xl">
            {liste.map((l) => (
              <SelectItem key={l} value={l} data-testid={`lista-option-${l}`} className="rounded-xl focus:bg-white/10 focus:text-white py-2.5">{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <div className="flex flex-col gap-3 pt-1">
        <Consent id="booking-privacy-checkbox" checked={values.privacyConsent} onChange={(v) => set("privacyConsent", v)}>
          Ho letto l'<Link to="/privacy" target="_blank" className="underline hover:text-white">informativa privacy</Link> e acconsento al trattamento dei miei dati per gestire la prenotazione. *
        </Consent>
        <Consent id="booking-marketing-checkbox" checked={values.marketingConsent} onChange={(v) => set("marketingConsent", v)}>
          Acconsento a ricevere comunicazioni su prossimi eventi via SMS o WhatsApp (facoltativo).
        </Consent>
      </div>
      {error && <p data-testid="booking-error" className="text-xs text-red-300 font-body">{error}</p>}
      <button type="submit" disabled={status === "submitting"} data-testid="booking-submit-button"
        className="bg-white text-black rounded-full px-6 py-3.5 text-sm font-medium font-body flex items-center justify-center gap-2 mt-2 transition-opacity hover:opacity-90 disabled:opacity-60">
        {status === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Prenota <ArrowUpRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
};
